import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import TopUpRequestTable from '../../../components/tables/TopUpRequestTable';

const formatDate = (date) => date.toISOString().split('T')[0];

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const superDistributorRequests = [
  {
    id: 'SDR0001',
    userName: 'Central Operations',
    date: '2025-10-30',
    amount: 125000,
    mode: 'NEFT',
    orderId: 'SDO981201',
    utr: 'SDAUTR10991',
    bank: 'HDFC Bank',
    transDate: '2025-10-29',
    remark: 'Monthly float replenishment',
    reason: '',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9191919191' },
    status: 'Submitted',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Pending',
    time: '08:55 AM',
  },
  {
    id: 'SDR0002',
    userName: 'Settlement Desk',
    date: '2025-10-28',
    amount: 98000,
    mode: 'RTGS',
    orderId: 'SDO981202',
    utr: 'SDAUTR10992',
    bank: 'Axis Bank',
    transDate: '2025-10-28',
    remark: 'High-volume DMT payouts',
    reason: '',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9262626262' },
    status: 'Approved',
    approvedBy: 'Finance Lead',
    approvedDate: '2025-10-28',
    recorn: 'Yes',
    time: '01:20 PM',
  },
  {
    id: 'SDR0003',
    userName: 'Finance Coordination',
    date: '2025-10-26',
    amount: 45000,
    mode: 'UPI',
    orderId: 'SDO981203',
    utr: 'SDAUTR10993',
    bank: 'ICICI Bank',
    transDate: '2025-10-25',
    remark: 'AEPS settlements buffer',
    reason: 'Supporting documents awaited',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9343434343' },
    status: 'Pending',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Follow-up',
    time: '05:05 PM',
  },
  {
    id: 'SDR0004',
    userName: 'Settlement Desk',
    date: '2025-10-24',
    amount: 152000,
    mode: 'IMPS',
    orderId: 'SDO981204',
    utr: 'SDAUTR10994',
    bank: 'State Bank of India',
    transDate: '2025-10-24',
    remark: 'Festival recharge demand',
    reason: '',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9111222333' },
    status: 'Submitted',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'In Review',
    time: '10:40 AM',
  },
  {
    id: 'SDR0005',
    userName: 'Central Operations',
    date: '2025-10-21',
    amount: 73000,
    mode: 'Cash Deposit',
    orderId: 'SDO981205',
    utr: 'SDAUTR10995',
    bank: 'Kotak Mahindra Bank',
    transDate: '2025-10-20',
    remark: 'Counter branch top-up',
    reason: '',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9556677889' },
    status: 'Approved',
    approvedBy: 'Finance Lead',
    approvedDate: '2025-10-21',
    recorn: 'Yes',
    time: '03:30 PM',
  },
  {
    id: 'SDR0006',
    userName: 'Finance Coordination',
    date: '2025-10-18',
    amount: 56000,
    mode: 'NEFT',
    orderId: 'SDO981206',
    utr: 'SDAUTR10996',
    bank: 'Union Bank of India',
    transDate: '2025-10-18',
    remark: 'Weekly liquidity restore',
    reason: '',
    parentId: { mobile: 'SUP-9822334455' },
    userId: { mobile: 'HQ-9777665544' },
    status: 'Pending',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Pending',
    time: '11:55 AM',
  },
];

const identifyDateRange = (records = []) => records.reduce(
  (range, record) => {
    const requestDate = parseDate(record?.date) || parseDate(record?.transDate);

    if (!requestDate) {
      return range;
    }

    const next = { ...range };

    if (!next.min || requestDate < next.min) {
      next.min = requestDate;
    }

    if (!next.max || requestDate > next.max) {
      next.max = requestDate;
    }

    return next;
  },
  { min: null, max: null }
);

const createDefaultFilters = (records) => {
  const range = identifyDateRange(records);
  const today = formatDate(new Date());

  return {
    fromDate: range.min ? formatDate(range.min) : today,
    toDate: range.max ? formatDate(range.max) : today,
    searchValue: '',
    pageNumber: 0,
    pageSize: 10,
  };
};

const SendTopUpRequest = () => {
  const defaultFilters = useMemo(() => createDefaultFilters(superDistributorRequests), []);

  const [filters, setFilters] = useState(defaultFilters);
  const [allRequests, setAllRequests] = useState(superDistributorRequests);
  const [requestsData, setRequestsData] = useState([]);
  const [meta, setMeta] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: defaultFilters.pageSize,
  });

  useEffect(() => {
    const from = parseDate(filters.fromDate);
    const to = parseDate(filters.toDate);
    const normalizedSearch = filters.searchValue?.trim().toLowerCase() ?? '';

    const filterByDate = (request) => {
      const requestDate = parseDate(request?.date) || parseDate(request?.transDate);

      if (!from && !to) {
        return true;
      }

      if (!requestDate) {
        return false;
      }

      if (from && requestDate < from) {
        return false;
      }

      if (to) {
        const normalizedTo = new Date(to);
        normalizedTo.setHours(23, 59, 59, 999);
        if (requestDate > normalizedTo) {
          return false;
        }
      }

      return true;
    };

    const includesSearchValue = (request) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchInValue = (value) => {
        if (value === null || value === undefined) {
          return false;
        }

        if (typeof value === 'object') {
          return Object.values(value).some((nested) => searchInValue(nested));
        }

        return String(value).toLowerCase().includes(normalizedSearch);
      };

      return Object.values(request).some(searchInValue);
    };

    const filtered = allRequests.filter((request) => filterByDate(request) && includesSearchValue(request));

    const totalRecords = filtered.length;
    const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / filters.pageSize) : 0;
    const lastPageIndex = Math.max(totalPages - 1, 0);
    const currentPage = Math.min(Math.max(filters.pageNumber, 0), lastPageIndex);
    const startIndex = currentPage * filters.pageSize;
    const pageSlice = filtered.slice(startIndex, startIndex + filters.pageSize);

    setRequestsData(pageSlice);
    setMeta({
      totalRecords,
      totalPages,
      currentPage,
      pageSize: filters.pageSize,
    });

    if (currentPage !== filters.pageNumber) {
      setFilters((prev) => ({ ...prev, pageNumber: currentPage }));
    }
  }, [allRequests, filters]);

  const handleDateChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      pageNumber: 0,
    }));
  };

  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      searchValue: value,
      pageNumber: 0,
    }));
  };

  const handleTableDataChange = (updatedPageData = []) => {
    if (!Array.isArray(updatedPageData)) {
      return;
    }

    setAllRequests((prev) => {
      const next = [...prev];
      const indexById = new Map(next.map((item, index) => [item.id, index]));

      updatedPageData.forEach((item) => {
        if (!item || !item.id) {
          return;
        }

        if (indexById.has(item.id)) {
          const idx = indexById.get(item.id);
          next[idx] = { ...next[idx], ...item };
        } else {
          next.unshift(item);
        }
      });

      return next;
    });
  };

  const handlePageChange = (pageNumber) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({
      ...prev,
      pageSize,
      pageNumber: 0,
    }));
  };

  return (
    <Container fluid>
      <TopUpRequestTable
        data={requestsData}
        loading={false}
        pagination={{
          totalRecords: meta.totalRecords,
          pageSize: filters.pageSize,
          currentPage: meta.currentPage,
          totalPages: meta.totalPages,
        }}
        onDataChange={handleTableDataChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={filters.searchValue}
        onSearch={handleSearch}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        onDateChange={handleDateChange}
        showActions={false}
      />
    </Container>
  );
};

export default SendTopUpRequest;

